import { useState } from 'react';
import { Col, Container, FloatingLabel, Form, Row, Stack } from 'react-bootstrap';
import JSONPretty from 'react-json-pretty';
import { useNavigate } from 'react-router-dom';
import { handleError, post } from '../../lib/API';
import { useAlert } from '../../providers/AlertProvider';
import { ButtonValues } from '../../types/button.type';
import { Message } from '../../types/message.type';
import Copy from '../buttons/Copy';
import Reset from '../buttons/Reset';
import Submit from '../buttons/Submit';
import './Format.css';

const customTheme = {
    main: 'line-height:1.3;color:#ffffff;background:#272822;overflow:auto;padding:1rem;border-radius:.375rem;white-space:pre-wrap;word-wrap:break-word;',
    error: 'line-height:1.3;color:#66d9ef;background:#272822;overflow:auto;',
    key: 'color:#a2fca2;',
    string: 'color:#fd971f;',
    value: 'color:#a6e22e;',
    boolean: 'color:#ac81fe;',
};

type Props = {
    inputLabel: string;
    buttons: ButtonValues[];
}

export default function Format({
    inputLabel,
    buttons,
}: Props) {
    const alert = useAlert();
    const navigate = useNavigate();
    const [processing, setProcessing] = useState(false);
    const [activeButton, setActiveButton] = useState(-1);
    const [outputLabel, setOutputLabel] = useState('Output');
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');

    const reset = () => {
        setInput('');
        setOutput('');
        setOutputLabel('Output');
        alert.clear();
    }

    const submit = (api: string, outputLabel: string, index: number) => {
        if (!input?.trim()) {
            return;
        }
        alert.clear();
        setActiveButton(index);
        setProcessing(true);
        setOutputLabel(outputLabel)
        const data: Message = { value: input };
        post<Message>(api, data)
            .then(response => setOutput(response.data.value))
            .catch(error => handleError(error, alert, navigate))
            .finally(() => setProcessing(false));
    }

    const isJson = (value: string) => {
        try {
            JSON.parse(value);
            return true;
        } catch {
            return false;
        }
    }

    return (
        <div>
            <Container fluid='true'>
                <Row xs={1} md={2}>
                    <Col>
                        <Form>
                            <FloatingLabel
                                controlId="floatingTextarea"
                                label={inputLabel}
                                className="mb-3"
                            >
                                <Form.Control
                                    as="textarea"
                                    placeholder={inputLabel}
                                    value={input}
                                    onChange={(event) => setInput(event.target.value)}
                                    style={{ height: 'calc(100vh - 15rem)' }}
                                    required
                                />
                            </FloatingLabel>
                            <Stack direction="horizontal" gap={2} className="mb-3">
                                {buttons.map((button, i) =>
                                    <Submit key={i}
                                        buttonText={button.buttonLabel}
                                        processing={processing && i === activeButton}
                                        onClick={() => submit(button.api, button.outputLabel, i)} />
                                )}
                                <Reset onClick={reset}/>
                            </Stack>
                        </Form>
                    </Col>
                    <Col>
                        {isJson(output)
                            ? <JSONPretty data={output} space={4} theme={customTheme} />
                            : <FloatingLabel
                                controlId="floatingTextarea"
                                label={outputLabel}
                                className="mb-3"
                            >
                                <Form.Control
                                    as="textarea"
                                    placeholder={outputLabel}
                                    value={output}
                                    onChange={(event) => setOutput(event.target.value)}
                                    style={{ height: 'calc(100vh - 15rem)' }}
                                />
                            </FloatingLabel>
                        }
                        <Copy text={output} />
                    </Col>
                </Row>
            </Container>
        </div>
    );
}